<template>
  <v-navigation-drawer
    v-model="internalValue"
    temporary
    right
    width="480"
    class="location-profile-drawer"
    style="z-index: 999; background-color: #ffffff !important;"
  >
    <div v-if="isLoading" class="d-flex justify-center align-center" style="height: 100vh;">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </div>
    
    <div v-else-if="locationData" class="location-profile-content">
      <!-- Header Section - Google Maps Style -->
      <div class="google-header">
        <!-- Close Button -->
        <v-btn
          icon
          class="close-btn"
          @click="internalValue = false"
        >
          <v-icon color="#5f6368">mdi-close</v-icon>
        </v-btn>

        <!-- Cover Photo (if available) -->
        <div 
          v-if="coverImageUrl"
          class="cover-image"
          :style="{ backgroundImage: `url(${coverImageUrl})` }"
        ></div>
        
        <!-- Business Header Info -->
        <div class="header-content">
          <div class="business-title-section">
            <h1 class="business-title">{{ businessName }}</h1>
            <div class="business-subtitle">{{ businessCategory }}</div>
            
            <!-- Rating Section -->
            <div class="rating-section">
              <div class="rating-display">
                <span class="rating-number">{{ averageRating?.toFixed(1) }}</span>
                <div class="rating-stars">
                  <v-icon
                    v-for="star in 5"
                    :key="star"
                    size="16"
                    :color="star <= Math.floor(averageRating) ? '#fbbc04' : '#e8eaed'"
                  >
                    mdi-star
                  </v-icon>
                </div>
                <span class="rating-count">({{ reviewCount }})</span>
              </div>
              
              <div class="business-type">{{ businessCategory }}</div>
            </div>

            <!-- Status Chips -->
            <div class="status-section" v-if="isOpen !== null || (openingHours && openingHours.length)">
              <span 
                v-if="isOpen !== null"
                class="status-chip"
                :class="{ 'open': isOpen, 'closed': !isOpen }"
              >
                {{ isOpen ? 'Open' : 'Closed' }}
              </span>
              <span v-if="isOpen !== null && openingHours && openingHours.length" class="status-separator">·</span>
              <span class="closes-text" v-if="openingHours && openingHours.length">
                {{ getCurrentDayHours() }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Content Sections -->
      <div class="profile-content">
        <!-- Contact Information -->
        <div class="info-section">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-information</v-icon>
            Contact Information
          </h3>
          
          <div class="info-item" v-if="address">
            <v-icon class="mr-3">mdi-map-marker</v-icon>
            <div>
              <div class="info-label">Address</div>
              <div class="info-value">{{ address }}</div>
            </div>
          </div>
          
          <div class="info-item" v-if="phone">
            <v-icon class="mr-3">mdi-phone</v-icon>
            <div>
              <div class="info-label">Phone</div>
              <div class="info-value">
                <a :href="`tel:${phone}`" class="contact-link">{{ phone }}</a>
              </div>
            </div>
          </div>
          
          <div class="info-item" v-if="website">
            <v-icon class="mr-3">mdi-web</v-icon>
            <div>
              <div class="info-label">Website</div>
              <div class="info-value">
                <a :href="website" target="_blank" class="contact-link">{{ formatWebsite(website) }}</a>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Description -->
        <div class="info-section" v-if="businessDescription">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-text</v-icon>
            About
          </h3>
          
          <div class="description-text">
            {{ businessDescription }}
          </div>
        </div>
        
        <!-- Opening Hours -->
        <div class="info-section" v-if="openingHours && openingHours.length">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-clock</v-icon>
            Hours
          </h3>
          
          <div class="hours-list">
            <div 
              v-for="(hours, index) in openingHours" 
              :key="index"
              class="hours-item"
              :class="{ 'current-day': isCurrentDay(hours.day) }"
            >
              <span class="day">{{ hours.day }}</span>
              <span class="time">{{ hours.hours || 'Closed' }}</span>
            </div>
          </div>
        </div>
        
        <!-- Services & Amenities -->
        <div class="info-section" v-if="hasAdditionalInfo">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-cog</v-icon>
            Services & Amenities
          </h3>
          
          <div v-for="(section, sectionName) in additionalInfo" :key="sectionName" class="service-section">
            <h4 class="service-category">{{ sectionName }}</h4>
            <div class="service-items">
              <v-chip
                v-for="(item, index) in section"
                :key="index"
                small
                outlined
                class="ma-1"
              >
                {{ getServiceLabel(item) }}
              </v-chip>
            </div>
          </div>
        </div>
        
        <!-- Popular Times (if available) -->
        <div class="info-section" v-if="popularTimes">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-chart-line</v-icon>
            Popular Times
          </h3>
          
          <div class="popular-times">
            <div v-if="popularTimes.liveText" class="live-status">
              <v-chip small color="primary">
                {{ popularTimes.liveText }} ({{ popularTimes.livePercent }}% busy)
              </v-chip>
            </div>
            
            <!-- Popular Times Chart -->
            <div v-if="popularTimes.histogram" class="popular-times-chart">
              <div v-for="(dayData, day) in popularTimes.histogram" :key="day" class="day-chart">
                <h4 class="day-name">{{ formatDayName(day) }}</h4>
                <div class="hours-chart">
                  <div 
                    v-for="hour in dayData" 
                    :key="hour.hour"
                    class="hour-bar"
                    :style="{ height: `${hour.occupancyPercent}%` }"
                    :title="`${hour.hour}:00 - ${hour.occupancyPercent}% busy`"
                  >
                    <div class="hour-label">{{ hour.hour }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Owner Updates -->
        <div class="info-section" v-if="ownerUpdates && ownerUpdates.length">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-account-tie</v-icon>
            Owner Updates
          </h3>
          
          <div class="updates-list">
            <div v-for="(update, index) in ownerUpdates" :key="index" class="update-item">
              <div class="update-content">
                <div class="update-text">{{ update.text || update.content }}</div>
                <div class="update-meta">
                  <span v-if="update.postDate" class="update-date">{{ update.postDate }}</span>
                  <span v-if="update.author" class="update-author">by {{ update.author }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Customer Updates -->
        <div class="info-section" v-if="customerUpdates">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-account-group</v-icon>
            Customer Updates
          </h3>
          
          <div class="customer-update">
            <div class="update-content">
              <div class="update-text">{{ customerUpdates.text }}</div>
              <div class="update-meta">
                <span v-if="customerUpdates.postDate" class="update-date">{{ customerUpdates.postDate }}</span>
                <span v-if="customerUpdates.postedBy?.name" class="update-author">
                  by {{ customerUpdates.postedBy.name }}
                  <span v-if="customerUpdates.postedBy.title" class="author-title">
                    ({{ customerUpdates.postedBy.title }})
                  </span>
                </span>
              </div>
              
              <!-- Customer Update Media -->
              <div v-if="customerUpdates.media && customerUpdates.media.length" class="update-media">
                <img 
                  v-for="(media, index) in customerUpdates.media.slice(0, 3)" 
                  :key="index"
                  :src="media.link" 
                  :alt="`Customer photo ${index + 1}`"
                  class="update-photo"
                  @error="handleImageError"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Review Tags -->
        <div class="info-section" v-if="reviewTags && reviewTags.length">
          <h3 class="section-title">
            <v-icon class="mr-2">mdi-tag-multiple</v-icon>
            Popular Review Topics
          </h3>
          
          <div class="review-tags">
            <v-chip
              v-for="tag in reviewTags.slice(0, 10)"
              :key="tag.title"
              small
              class="ma-1"
            >
              {{ tag.title }} ({{ tag.count }})
            </v-chip>
          </div>
        </div>
        
        <!-- Google-Style Action Buttons -->
        <div class="action-buttons-section">
          <div class="action-buttons-row">
            <button class="action-btn">
              <v-icon color="#1a73e8" size="24">mdi-directions</v-icon>
              <span>Directions</span>
            </button>
            <button class="action-btn" v-if="phone" @click="callBusiness">
              <v-icon color="#1a73e8" size="24">mdi-phone</v-icon>
              <span>Call</span>
            </button>
            <button class="action-btn" v-if="website" @click="visitWebsite">
              <v-icon color="#1a73e8" size="24">mdi-web</v-icon>
              <span>Website</span>
            </button>
            <button class="action-btn">
              <v-icon color="#1a73e8" size="24">mdi-share-variant</v-icon>
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="d-flex justify-center align-center" style="height: 100vh;">
      <div class="text-center">
        <v-icon size="48" color="grey">mdi-alert-circle</v-icon>
        <div class="mt-2">Failed to load location data</div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import { doc, getDoc } from "firebase/firestore";

export default {
  name: "LocationProfileDrawer",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    locationId: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      locationData: null,
      apifyProfile: null
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
        // Clear data when drawer closes to ensure fresh fetch next time
        if (!val) {
          setTimeout(() => {
            this.locationData = null;
            this.apifyProfile = null;
          }, 300); // Small delay to allow drawer close animation
        }
      }
    },
    
    // Business Basic Info
    businessName() {
      return this.apifyProfile?.complete_profile?.title || 
             this.locationData?.name || 
             this.locationData?.storeLocation || 
             'Unknown Business';
    },
    
    businessCategory() {
      return this.apifyProfile?.complete_profile?.categoryName || 
             this.locationData?.gmbProfile?.categories?.primaryCategory?.displayName ||
             this.locationData?.gmb_shallow_profile_data?.primary_category ||
             'Business';
    },
    
    averageRating() {
      return this.apifyProfile?.complete_profile?.totalScore || 
             this.locationData?.averageRating || 
             0;
    },
    
    reviewCount() {
      return this.apifyProfile?.complete_profile?.reviewsCount || 
             this.locationData?.reviewCount || 
             0;
    },
    
    // Contact Information
    address() {
      return this.apifyProfile?.complete_profile?.address || 
             this.locationData?.address ||
             [this.locationData?.sub_locality, this.locationData?.city, this.locationData?.state, this.locationData?.zipcode]
               .filter(Boolean).join(', ') ||
             'Address not available';
    },
    
    phone() {
      return this.apifyProfile?.complete_profile?.phone || 
             this.locationData?.phone ||
             this.locationData?.gmbProfile?.phoneNumbers?.primaryPhone ||
             this.locationData?.gmb_shallow_profile_data?.phone;
    },
    
    website() {
      return this.apifyProfile?.complete_profile?.website || 
             this.locationData?.website ||
             this.locationData?.gmbProfile?.websiteUri ||
             this.locationData?.gmb_shallow_profile_data?.website;
    },
    
    // Images
    logoUrl() {
      return this.locationData?.gmbProfile?.mediaItems?.logo?.googleUrl ||
             this.locationData?.gmb_shallow_profile_data?.logo ||
             this.locationData?.logo ||
             null;
    },
    
    coverImageUrl() {
      if (this.apifyProfile?.complete_profile?.imageUrls && this.apifyProfile.complete_profile.imageUrls.length > 0) {
        return this.apifyProfile.complete_profile.imageUrls[0];
      }
      return this.apifyProfile?.complete_profile?.imageUrl ||
             this.locationData?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl ||
             this.locationData?.gmb_shallow_profile_data?.cover_photo ||
             this.locationData?.cover_photo ||
             null;
    },
    
    // Status
    isOpen() {
      if (this.apifyProfile?.complete_profile?.temporarilyClosed) return false;
      if (this.apifyProfile?.complete_profile?.permanentlyClosed) return false;
      // You could add logic to determine if open based on current time and opening hours
      return null; // Unknown
    },
    
    verificationStatus() {
      return this.locationData?.gmbProfile?.verificationState ||
             this.locationData?.verification_state;
    },
    
    placeId() {
      return this.apifyProfile?.complete_profile?.placeId || 
             this.locationData?.placeId;
    },
    
    // Opening Hours
    openingHours() {
      if (this.apifyProfile?.complete_profile?.openingHours) {
        return this.apifyProfile.complete_profile.openingHours;
      }
      
      // Convert GMB format to display format
      if (this.locationData?.gmbProfile?.regularHours?.periods) {
        return this.convertGMBHours(this.locationData.gmbProfile.regularHours.periods);
      }
      
      if (this.locationData?.gmb_shallow_profile_data?.opening_hours) {
        return this.locationData.gmb_shallow_profile_data.opening_hours;
      }
      
      return [];
    },
    
    // Additional Information
    additionalInfo() {
      return this.apifyProfile?.complete_profile?.additionalInfo || {};
    },
    
    hasAdditionalInfo() {
      return Object.keys(this.additionalInfo).length > 0;
    },
    
    // Popular Times
    popularTimes() {
      if (this.apifyProfile?.complete_profile?.popularTimesLiveText) {
        return {
          liveText: this.apifyProfile.complete_profile.popularTimesLiveText,
          livePercent: this.apifyProfile.complete_profile.popularTimesLivePercent,
          histogram: this.apifyProfile.complete_profile.popularTimesHistogram
        };
      }
      return null;
    },
    
    // Review Tags
    reviewTags() {
      return this.apifyProfile?.complete_profile?.reviewsTags || [];
    },
    
    // Business Description
    businessDescription() {
      return this.apifyProfile?.complete_profile?.description || 
             this.locationData?.gmbProfile?.profile?.description ||
             this.locationData?.description;
    },
    
    // Owner Updates
    ownerUpdates() {
      return this.apifyProfile?.complete_profile?.ownerUpdates || [];
    },
    
    // Customer Updates
    customerUpdates() {
      return this.apifyProfile?.complete_profile?.updatesFromCustomers;
    }
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.locationId) {
          this.fetchLocationData();
        }
      }
    },
    
    locationId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.value) {
          this.fetchLocationData();
        }
      }
    }
  },
  
  mounted() {
    // Fetch data immediately if drawer is open and locationId is available
    if (this.value && this.locationId) {
      this.fetchLocationData();
    }
  },
  
  methods: {
    async fetchLocationData() {
      if (!this.locationId || !this.userId) {
        console.log("Missing locationId or userId:", { locationId: this.locationId, userId: this.userId });
        return;
      }
      
      console.log("Fetching location data for:", { locationId: this.locationId, userId: this.userId });
      this.isLoading = true;
      
      try {
        // Fetch location workspace data
        const locationRef = doc(db, "users", this.userId, "fly_workspaces", this.locationId);
        console.log("Fetching location from path:", locationRef.path);
        const locationSnap = await getDoc(locationRef);
        
        if (locationSnap.exists()) {
          this.locationData = { id: locationSnap.id, ...locationSnap.data() };
          console.log("Location data fetched:", this.locationData);
        } else {
          console.log("Location document does not exist");
        }
        
        // Fetch Apify profile data
        try {
          const apifyRef = doc(db, "users", this.userId, "fly_workspaces", this.locationId, "gmb_locations_apify_profiles", this.locationId);
          console.log("Fetching Apify profile from path:", apifyRef.path);
          const apifySnap = await getDoc(apifyRef);
          
          if (apifySnap.exists()) {
            this.apifyProfile = apifySnap.data();
            console.log("Apify profile data fetched:", this.apifyProfile);
            console.log("Complete profile exists:", !!this.apifyProfile?.complete_profile);
            if (this.apifyProfile?.complete_profile) {
              console.log("Complete profile data:", this.apifyProfile.complete_profile);
            }
          } else {
            console.log("Apify profile document does not exist at path:", apifyRef.path);
            
            // Try alternative path structure if the first one doesn't work
            const altApifyRef = doc(db, "users", this.userId, "fly_workspaces", this.locationId, "gmb_locations_apify_profiles", "profile");
            console.log("Trying alternative Apify path:", altApifyRef.path);
            const altApifySnap = await getDoc(altApifyRef);
            
            if (altApifySnap.exists()) {
              this.apifyProfile = altApifySnap.data();
              console.log("Apify profile data fetched from alternative path:", this.apifyProfile);
            } else {
              console.log("No Apify profile found in alternative path either");
            }
          }
        } catch (apifyError) {
          console.error("Error fetching Apify profile data:", apifyError);
        }
        
      } catch (error) {
        console.error("Error fetching location data:", error);
      } finally {
        this.isLoading = false;
        console.log("Fetch complete. Final data state:", { 
          locationData: !!this.locationData, 
          apifyProfile: !!this.apifyProfile 
        });
      }
    },
    
    formatWebsite(website) {
      return website?.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '') || '';
    },
    
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    
    isCurrentDay(day) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = days[new Date().getDay()];
      return day === today;
    },
    
    convertGMBHours(periods) {
      // Convert GMB API format to readable format
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const convertedHours = [];
      
      // This is a simplified conversion - you might need more complex logic
      periods.forEach(period => {
        if (period.open && period.close) {
          const dayName = days[period.open.day];
          const openTime = this.formatTime(period.open.time);
          const closeTime = this.formatTime(period.close.time);
          
          convertedHours.push({
            day: dayName,
            hours: `${openTime} to ${closeTime}`
          });
        }
      });
      
      return convertedHours;
    },
    
    formatTime(time) {
      // Convert HHMM format to readable time
      if (!time || time.length !== 4) return time;
      
      const hours = parseInt(time.substr(0, 2));
      const minutes = time.substr(2, 2);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      
      return `${displayHours}:${minutes} ${ampm}`;
    },
    
    getServiceLabel(item) {
      if (typeof item === 'object') {
        const key = Object.keys(item)[0];
        const value = item[key];
        return value === true ? key : `${key}: ${value}`;
      }
      return item;
    },
    
    formatDayName(dayCode) {
      const dayMap = {
        'Su': 'Sunday',
        'Mo': 'Monday', 
        'Tu': 'Tuesday',
        'We': 'Wednesday',
        'Th': 'Thursday',
        'Fr': 'Friday',
        'Sa': 'Saturday'
      };
      return dayMap[dayCode] || dayCode;
    },
    
    callBusiness() {
      if (this.phone) {
        window.open(`tel:${this.phone}`);
      }
    },
    
    visitWebsite() {
      if (this.website) {
        window.open(this.website, '_blank');
      }
    },
    
    getCurrentDayHours() {
      if (!this.openingHours || !this.openingHours.length) {
        return '';
      }
      
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = days[new Date().getDay()];
      
      const todayHours = this.openingHours.find(h => h.day === today);
      if (todayHours && todayHours.hours) {
        // Extract closing time from hours like "9 AM to 9 PM"
        const match = todayHours.hours.match(/to (\d+(?::\d+)?\s*[AP]M)/i);
        if (match) {
          return `Closes ${match[1].toLowerCase()}`;
        }
      }
      
      return 'Hours available';
    }
  }
};
</script>

<style scoped>
.location-profile-drawer {
  position: fixed !important;
  background-color: #ffffff !important;
}

/* Override Vuetify drawer styles */
.location-profile-drawer >>> .v-navigation-drawer__content {
  background-color: #ffffff !important;
}

.location-profile-content {
  height: 100vh;
  overflow-y: auto;
  background-color: #ffffff !important;
}

/* Google Maps Header Styling */
.google-header {
  position: relative;
  background: #fff;
  border-bottom: 1px solid #e8eaed;
}

.close-btn {
  position: absolute !important;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
}

.cover-image {
  height: 200px;
  background-size: cover;
  background-position: center;
  width: 100%;
}

.header-content {
  padding: 16px 24px 20px;
  background-color: #ffffff !important;
}

.business-title {
  font-size: 22px;
  font-weight: 400;
  color: #202124 !important;
  margin: 0 0 4px 0;
  line-height: 28px;
}

.business-subtitle {
  font-size: 14px;
  color: #5f6368 !important;
  margin-bottom: 8px;
}

.rating-section {
  margin-bottom: 12px;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.rating-number {
  font-size: 14px;
  color: #202124 !important;
  font-weight: 500;
}

.rating-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.rating-count {
  font-size: 14px;
  color: #5f6368 !important;
}

.business-type {
  font-size: 14px;
  color: #5f6368 !important;
}

.status-section {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.status-chip {
  font-size: 14px;
  font-weight: 500;
}

.status-chip.open {
  color: #137333 !important;
}

.status-chip.closed {
  color: #d93025 !important;
}

.status-separator {
  color: #5f6368 !important;
  font-size: 14px;
}

.closes-text {
  font-size: 14px;
  color: #5f6368 !important;
}

.profile-content {
  padding: 0;
  background-color: #ffffff !important;
}

/* Google-Style Action Buttons */
.action-buttons-section {
  padding: 0 24px;
  border-bottom: 1px solid #e8eaed;
}

.action-buttons-row {
  display: flex;
  justify-content: space-around;
  padding: 16px 0;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color 0.2s;
  min-width: 80px;
}

.action-btn:hover {
  background-color: #f8f9fa;
}

.action-btn span {
  font-size: 12px;
  color: #1a73e8;
  font-weight: 500;
}

/* Google-Style Info Sections */
.info-section {
  padding: 24px;
  border-bottom: 1px solid #e8eaed;
  background-color: #ffffff !important;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #202124 !important;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  color: #5f6368 !important;
  font-weight: 400;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #202124 !important;
  line-height: 20px;
}

.contact-link {
  color: #1a73e8;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.hours-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hours-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}

.current-day {
  font-weight: 500;
  color: #202124 !important;
}

.day {
  color: #202124 !important;
  font-weight: 400;
}

.time {
  color: #5f6368 !important;
  font-weight: 400;
}

.service-section {
  margin-bottom: 20px;
}

.service-category {
  font-size: 16px;
  font-weight: 500;
  color: #202124 !important;
  margin-bottom: 12px;
}

.service-items {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

/* Google-style service items */
.service-items .v-chip {
  background-color: transparent !important;
  color: #202124 !important;
  border: none !important;
  padding: 8px 0 !important;
  height: auto !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  justify-content: flex-start !important;
  border-radius: 0 !important;
}

.service-items .v-chip::before {
  content: "✓";
  color: #137333;
  margin-right: 8px;
  font-weight: 500;
}

.popular-times {
  text-align: center;
}

.live-status {
  margin-bottom: 16px;
}

.review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Google-style review tag chips */
.review-tags .v-chip {
  background-color: #f8f9fa !important;
  color: #5f6368 !important;
  border: 1px solid #dadce0 !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  height: 32px !important;
  padding: 0 12px !important;
  border-radius: 16px !important;
}

/* Description Section */
.description-text {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #202124 !important;
}

/* Popular Times Chart */
.popular-times-chart {
  margin-top: 16px;
}

.day-chart {
  margin-bottom: 16px;
}

.day-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #202124 !important;
  margin-bottom: 8px;
}

.hours-chart {
  display: flex;
  align-items: end;
  height: 60px;
  gap: 2px;
  padding: 4px;
  background: #f8f9fa;
  border-radius: 4px;
}

.hour-bar {
  flex: 1;
  background: linear-gradient(to top, #4CAF50, #81C784);
  border-radius: 2px;
  min-height: 4px;
  position: relative;
  transition: background 0.2s;
}

.hour-bar:hover {
  background: linear-gradient(to top, #388E3C, #66BB6A);
}

.hour-label {
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.7rem;
  color: #666;
  white-space: nowrap;
}

/* Google-Style Updates Sections */
.updates-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.update-item,
.customer-update {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.update-content {
  width: 100%;
}

.update-text {
  font-size: 14px;
  line-height: 20px;
  color: #202124 !important;
  margin-bottom: 8px;
}

.update-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  color: #5f6368 !important;
}

.update-date {
  font-weight: 400;
  color: #5f6368 !important;
}

.update-author {
  font-weight: 400;
  color: #5f6368 !important;
}

.author-title {
  color: #5f6368 !important;
}

.update-media {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.update-photo {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #dadce0;
  transition: transform 0.2s;
}

.update-photo:hover {
  transform: scale(1.02);
}
</style> 